import { NavLink, Navigate, Outlet, useLocation, Link } from "react-router-dom";
import { Inbox, Settings as Cog, Mail, LogOut, ExternalLink } from "lucide-react";
import { useAuth } from "@/lib/auth";

const ITEMS = [
  { to: "/admin/leads", label: "Leads", icon: Inbox },
  { to: "/admin/settings", label: "Settings", icon: Cog },
  { to: "/admin/templates", label: "Email templates", icon: Mail },
];

export default function AdminLayout() {
  const { admin, loading, logout } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  }
  if (!admin) {
    return <Navigate to="/admin/login" state={{ from: location.pathname }} replace />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="flex flex-col lg:flex-row">
        {/* Sidebar */}
        <aside className="lg:w-64 lg:fixed lg:inset-y-0 lg:left-0 border-r border-border bg-card flex flex-col">
          <div className="px-6 pt-6 pb-6 border-b border-border">
            <Link to="/" className="flex items-baseline gap-2">
              <span className="font-display text-xl font-extrabold tracking-tight">RSR</span>
              <span className="overline">Admin</span>
            </Link>
            <p className="mt-2 text-xs text-muted-foreground truncate">{admin.email}</p>
          </div>
          <nav className="flex-1 px-3 py-5 flex lg:flex-col gap-1 overflow-x-auto" data-testid="admin-nav">
            {ITEMS.map(i => (
              <NavLink
                key={i.to}
                to={i.to}
                data-testid={`admin-nav-${i.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 text-sm font-semibold transition-colors whitespace-nowrap ${
                    isActive ? "bg-accent text-white" : "text-foreground/75 hover:text-foreground hover:bg-secondary"
                  }`}
              >
                <i.icon className="w-4 h-4" strokeWidth={1.7} />
                {i.label}
              </NavLink>
            ))}
          </nav>
          <div className="px-3 py-4 border-t border-border space-y-1">
            <Link
              to="/"
              target="_blank"
              className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-foreground/70 hover:text-foreground hover:bg-secondary transition-colors"
            >
              <ExternalLink className="w-4 h-4" strokeWidth={1.7} />
              View site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-foreground/70 hover:text-foreground hover:bg-secondary transition-colors"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.7} />
              Sign out
            </button>
          </div>
        </aside>

        <main className="flex-1 lg:ml-64">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
